import { cn } from '@eureca/ui';
import { AddFaqDialog, type FaqDialogMode } from '../program-create-faqs/AddFaqDialog';
import {
  DefaultFaqsPanel,
  type DefaultFaqsPanelItem,
} from '@/shared/default-faqs/DefaultFaqsPanel';
import {
  DEFAULT_FAQS_CARD_TITLE,
  defaultFaqsCardCopy,
} from '@/shared/default-faqs/default-faqs-card-copy';
import {
  PlusIcon,
  SquarePenIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from '../program-create-faqs/icons';
import { ProgramEditShell, type ProgramEditShellProps } from '../program-edit/ProgramEditShell';
import { BlockedEditTooltip } from '../program-edit/BlockedEditTooltip';
import { RichTextContent } from '@/shared/rich-text-content/RichTextContent';

export interface ProgramFaq {
  id: string;
  question: string;
  answer: string;
}

export interface ProgramEditFaqsPageProps extends Omit<
  ProgramEditShellProps,
  'subtitle' | 'activeStep' | 'children'
> {
  faqs: readonly ProgramFaq[];
  onAddFaq: () => void;
  onEditFaq: (id: string) => void;
  onRemoveFaq: (id: string) => void;
  onViewDefaultFaqs: () => void;

  isDefaultFaqsOpen: boolean;
  onDefaultFaqsOpenChange: (open: boolean) => void;
  /** EU-641 — itens + estados do painel de FAQs padrão (dados do BFF). */
  defaultFaqItems: DefaultFaqsPanelItem[];
  defaultFaqsLoading?: boolean;
  defaultFaqsError?: boolean;
  onDefaultFaqsRetry?: () => void;
  onDefaultFaqToggle: (faqId: number, hidden: boolean) => void;

  isAddDialogOpen: boolean;
  onAddDialogOpenChange: (open: boolean) => void;
  dialogMode: FaqDialogMode;
  newFaqQuestion: string;
  newFaqAnswer: string;
  onNewFaqQuestionChange: (value: string) => void;
  onNewFaqAnswerChange: (value: string) => void;
  onSaveNewFaq: () => void;
}

export function ProgramEditFaqsPage({
  faqs,
  onAddFaq,
  onEditFaq,
  onRemoveFaq,
  onViewDefaultFaqs,
  isDefaultFaqsOpen,
  onDefaultFaqsOpenChange,
  defaultFaqItems,
  defaultFaqsLoading,
  defaultFaqsError,
  onDefaultFaqsRetry,
  onDefaultFaqToggle,
  isAddDialogOpen,
  onAddDialogOpenChange,
  dialogMode,
  newFaqQuestion,
  newFaqAnswer,
  onNewFaqQuestionChange,
  onNewFaqAnswerChange,
  onSaveNewFaq,
  ...shellProps
}: ProgramEditFaqsPageProps) {
  // EU-570 — Encerrado/Cancelado: "Adicionar nova pergunta" e os cards
  // (editar/excluir) bloqueados, com o tooltip "Edição bloqueada". O fieldset
  // do shell desabilita os botões nativos.
  const readOnly = shellProps.readOnly ?? false;
  return (
    <>
      <ProgramEditShell
        {...shellProps}
        subtitle="Perguntas Frequentes"
        activeStep="faqs"
        afterFieldset={
          /* QA EU-641 — o card vive FORA do fieldset do EU-570: o link
             "Conferir perguntas" abre o drawer (leitura) mesmo em programa
             Encerrado/Cancelado — um <button> dentro de fieldset disabled
             fica inerte. Espelha a vaga, onde o card já fica fora. */
          <div
            role="note"
            className={cn(
              'flex flex-row items-start gap-3 rounded-lg border border-border bg-card p-4'
            )}
          >
            <div className="flex shrink-0 pt-0.5 text-foreground">
              <TriangleAlertIcon className="size-4" />
            </div>
            <div className="flex flex-1 flex-col gap-1">
              {/* EU-641 — copy por status (FQC-024) + rótulos padronizados. */}
              <p className="text-sm font-medium leading-5 text-foreground">
                {DEFAULT_FAQS_CARD_TITLE}
              </p>
              <p className="text-sm leading-5 text-muted-foreground">
                {defaultFaqsCardCopy('program', readOnly).body}{' '}
                <button
                  type="button"
                  onClick={onViewDefaultFaqs}
                  className="font-medium text-primary hover:underline"
                  data-testid="program-faqs-conferir-default"
                >
                  {defaultFaqsCardCopy('program', readOnly).linkLabel}
                </button>
              </p>
            </div>
          </div>
        }
      >
        <div className="flex flex-col gap-5">
          <BlockedEditTooltip enabled={readOnly}>
            <button
              type="button"
              onClick={onAddFaq}
              disabled={readOnly}
              className="flex w-full flex-row items-center justify-between gap-2 rounded-2xl border border-border bg-card p-5 text-left shadow-sm transition-colors hover:border-primary disabled:opacity-60 disabled:hover:border-border"
            >
              <span className="text-base font-medium leading-none text-foreground">
                Adicionar nova pergunta
              </span>
              <PlusIcon className="size-5 text-foreground" />
            </button>
          </BlockedEditTooltip>

          {faqs.map((faq) => (
            <BlockedEditTooltip key={faq.id} enabled={readOnly}>
              <FaqCard
                faq={faq}
                readOnly={readOnly}
                onEdit={() => onEditFaq(faq.id)}
                onRemove={() => onRemoveFaq(faq.id)}
              />
            </BlockedEditTooltip>
          ))}
        </div>
      </ProgramEditShell>

      <AddFaqDialog
        open={isAddDialogOpen}
        onOpenChange={onAddDialogOpenChange}
        mode={dialogMode}
        question={newFaqQuestion}
        answer={newFaqAnswer}
        onQuestionChange={onNewFaqQuestionChange}
        onAnswerChange={onNewFaqAnswerChange}
        onSave={onSaveNewFaq}
      />

      {/* EU-641 — painel com switches; persistência imediata na edição;
          programa terminal → switches desabilitados (FQC-023). */}
      <DefaultFaqsPanel
        open={isDefaultFaqsOpen}
        onOpenChange={onDefaultFaqsOpenChange}
        items={defaultFaqItems}
        loading={defaultFaqsLoading}
        error={defaultFaqsError}
        onRetry={onDefaultFaqsRetry}
        disabled={readOnly}
        onToggle={onDefaultFaqToggle}
      />
    </>
  );
}

interface FaqCardProps {
  faq: ProgramFaq;
  readOnly?: boolean;
  onEdit: () => void;
  onRemove: () => void;
}

function FaqCard({ faq, readOnly = false, onEdit, onRemove }: FaqCardProps) {
  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div className="flex flex-row items-center gap-2">
        <span className="flex-1 text-base font-medium leading-none text-foreground">
          {faq.question}
        </span>
        <button
          type="button"
          onClick={onEdit}
          disabled={readOnly}
          aria-label={`Editar pergunta "${faq.question}"`}
          className="text-foreground transition-colors hover:text-primary disabled:opacity-50 disabled:hover:text-foreground"
        >
          <SquarePenIcon className="size-5" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={readOnly}
          aria-label={`Remover pergunta "${faq.question}"`}
          className="text-foreground transition-colors hover:text-destructive disabled:opacity-50 disabled:hover:text-foreground"
        >
          <Trash2Icon className="size-5" />
        </button>
      </div>
      <RichTextContent html={faq.answer} />
    </div>
  );
}
